import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { HttpErrorResponse } from '@angular/common/http';
import { Company, CompanySearchElement } from '@shared/models/data-model';
import { RegistrationHelper } from '@shared/registration-helper';
import { AmplitudeService } from '@shared/services/amplitude.service';
import { CompanySearchMode, CompanySearchService } from '@shared/services/company-search.service';
import { ModalDialogService } from '@shared/services/modal-dialog.service';
import { RegistrationService } from '@shared/services/registration.service';
import { REGISTRATION_STEP_ID } from '@shared/services/steps.service';
import { NGXLogger } from 'ngx-logger';
import { DTRUM_ACTION_NAME, DynatraceHelperService } from '@shared/services/dynatraceHelper.service';
import { COMPANY_SEARCH_CONFIG, CompanySearchModeConfig, CountrySearchConfig } from './company-search.config';
import { CompanySearchDetailModalComponent } from '@shared/dialog-contents/company-search-detail-modal/company-search-detail-modal.component';

export type CompanySearchError = 'noResults' | 'invalidFormat' | 'tooManyResults' | 'providerUnreachable';

type CompanySearchForm = {
    mode: FormControl<CompanySearchMode>;
    term: FormControl<string>;
    result: FormControl<string>;
};

@Component({
    selector: 'company-search',
    templateUrl: './company-search.component.html',
    styleUrl: './company-search.component.scss'
})
export class CompanySearchComponent implements OnInit {
    countryIso3: string;
    countryConfig: CountrySearchConfig;
    form: FormGroup<CompanySearchForm>;

    searchedTerm = '';
    isSearchLoading = false;
    isDownloadLoading = false;
    results: CompanySearchElement[] = [];
    error: CompanySearchError | null = null;

    get isLoading(): boolean {
        return this.isSearchLoading || this.isDownloadLoading;
    }

    get hasBothModes(): boolean {
        return !!(this.countryConfig?.searchByName && this.countryConfig?.searchByNumber);
    }

    get modeConfig(): CompanySearchModeConfig {
        const mode: CompanySearchMode = this.form?.controls.mode?.value ?? 'name';
        return mode === 'name' ? this.countryConfig?.searchByName : this.countryConfig?.searchByNumber;
    }

    constructor(
        private readonly route: ActivatedRoute,
        private readonly router: Router,
        private readonly fb: FormBuilder,
        private readonly registrationService: RegistrationService,
        private readonly amplitudeService: AmplitudeService,
        private readonly companySearchService: CompanySearchService,
        private readonly modalDialogService: ModalDialogService,
        private readonly logger: NGXLogger,
        private readonly dynatrace: DynatraceHelperService
    ) { }

    ngOnInit() {
        const company: Company = this.route.snapshot.data.company;
        this.countryIso3 = company?.registeredAddress?.countryISO;
        this.countryConfig = COMPANY_SEARCH_CONFIG[this.countryIso3] ?? COMPANY_SEARCH_CONFIG['fallback'];

        const initialMode: CompanySearchMode = this.countryConfig.searchByName ? 'name' : 'registrationNumber';

        this.form = this.fb.group({
            mode: [initialMode],
            term: ['', Validators.minLength(3)],
            result: ['']
        });

        this.form.controls.mode.valueChanges.subscribe(() => {
            this.form.controls.term.reset('');
            this.form.controls.result.reset('');
            this.results = [];
            this.error = null;
        });

        this.form.controls.result.valueChanges.subscribe((kybProviderId: string ) => {
            const result = this.results?.find(r => r.kybProviderId === kybProviderId);
            if(kybProviderId && result) {
                this.selectResult(result);
            }
        });
    }

    async search() {
        if (this.isSearchLoading) return;

        this.form.controls.term.markAsTouched();
        if (this.form.controls.term.invalid) return;

        const term: string = this.form.controls.term.value?.trim();
        if (!term) return;

        const searchMode = this.form.controls.mode.value;
        this.searchedTerm = term;
        this.isSearchLoading = true;
        this.form.controls.mode.disable({ emitEvent: false });
        this.form.controls.term.disable({ emitEvent: false });
        this.results = [];
        this.error = null;

        const actionId = this.dynatrace.enterAction(DTRUM_ACTION_NAME.COMPANY_SEARCH);
        try {
            const result = await this.companySearchService.search(this.searchedTerm, this.countryIso3, searchMode);
            const numResults = result?.companies?.length ?? 0;
            this.amplitudeService.trackCompanySearchPerformed(this.searchedTerm.length, numResults, searchMode === 'registrationNumber');
            if (numResults === 0) {
                this.error = 'noResults';
            } else {
                this.results = result.companies;
            }
        } catch (e) {
            this.error = this.classifyError(e);
            this.amplitudeService.trackCompanySearchFailed(this.error, searchMode, this.searchedTerm.length, this.countryIso3);
        } finally {
            this.isSearchLoading = false;
            this.form.controls.term.enable({ emitEvent: false });
            this.form.controls.mode.enable({ emitEvent: false });
            this.dynatrace.leaveAction(actionId);
        }
    }

    private classifyError(e: unknown): CompanySearchError {
        if (e instanceof HttpErrorResponse) {
            if (e.status === 462) return 'invalidFormat';
            if (e.status === 463) return 'tooManyResults';
        }
        this.logger.error('Company search failed', e);
        return 'providerUnreachable';
    }

    async selectResult(element: CompanySearchElement) {
        this.isDownloadLoading = true;
        this.form.controls.result.reset();
        this.form.disable({ emitEvent: false });
        this.error = null;

        const actionId = this.dynatrace.enterAction(DTRUM_ACTION_NAME.COMPANY_SEARCH);
        try {
            const lookupResult = await this.companySearchService.lookup(element.kybProviderId, this.countryIso3);
            this.companySearchService.setCompanyLookupResult(lookupResult);

            this.modalDialogService.showModalDialog(CompanySearchDetailModalComponent, {
                withReturnValue: true,
                title: 'MODAL_CONTENTS.COMPANY_SEARCH_DETAIL.TITLE',
                acceptLabel: 'MODAL_CONTENTS.COMPANY_SEARCH_DETAIL.CONFIRM',
                cancelLabel: 'MODAL_CONTENTS.COMPANY_SEARCH_DETAIL.DISMISS'
            }).subscribe(confirmed => {
                if (confirmed) {
                    this.saveAndNavigate(actionId).catch(err => this.logger.error('Save failed', err));
                } else {
                    this.amplitudeService.trackCompanySearchResultDiscarded(this.countryIso3);
                    this.dynatrace.leaveAction(actionId);
                }
            });
        } catch (e) {
            this.logger.error('Company lookup failed', e);
            this.amplitudeService.trackCompanyLookupFailed(this.countryIso3);
            this.error = 'providerUnreachable';
            this.dynatrace.leaveAction(actionId);
        } finally {
            this.isDownloadLoading = false;
            if (!this.isLoading) {
                this.form.enable({ emitEvent: false });
            }
        }
    }

    private async saveAndNavigate(dtrumActionId?: number) {
        const company: Company = RegistrationHelper.cloneCompany(this.route.snapshot.data.company);
        const result = this.companySearchService.getCachedLookupResult();

        company.kybProviderId = result.kybProviderId;
        company.registrationNumber = result.registrationNumber;
        company.registeredName = result.name;
        company.vatNumber = result.vatNumber;
        company.registeredAddress = {
            line1: result.registeredAddress?.line1,
            line2: result.registeredAddress?.line2,
            postalCode: result.registeredAddress?.postalCode,
            city: result.registeredAddress?.city,
            province: result.registeredAddress?.province,
            countryISO: company.registeredAddress.countryISO
        };

        await this.registrationService.saveCompany(company);
        this.registrationService.completeStep(REGISTRATION_STEP_ID.COMPANY_SEARCH);
        this.router.navigate([this.registrationService.getNextStep()])
            .then(() => this.dynatrace.leaveAction(dtrumActionId));
    }

    enterManually() {
        this.amplitudeService.trackCompanyInputModeSwitched(this.countryIso3, 'toManual');
        const actionId = this.dynatrace.enterAction(DTRUM_ACTION_NAME.COMPANY_SWITCH_TO_MANUAL);
        this.router.navigate([this.registrationService.selectManualCompanyInputMode()])
            .then(() => this.dynatrace.leaveAction(actionId));
    }

    previousStep() {
        const actionId = this.dynatrace.enterAction(DTRUM_ACTION_NAME.BACK_TO_PREVIOUS);
        this.router.navigate([this.registrationService.getPreviousStep()])
            .then(() => this.dynatrace.leaveAction(actionId));
    }
}
